import PptxGenJS from 'pptxgenjs';
import { clamp, pxToIn, roundNumberToDoubleDecimal } from '@app/services/export/ppt/utils/formattingUtils';
import IImage from '@app/types/data/base/IImage';
import { colors, elementSizes, presentationDimensions } from '@app/services/export/ppt/variables/constants';
import { copyBold } from '@app/services/export/ppt/variables/typography';
import Coord = PptxGenJS.Coord;
import HyperlinkProps = PptxGenJS.HyperlinkProps;

export interface ISlideImageProps {
	destinationWidth: number; // inches
	destinationHeight?: number; // inches
	x?: Coord;
	y?: Coord;
	type?: 'contain' | 'cover';
	rounding?: boolean;
	fallbackExtension?: string;
	hyperlink?: HyperlinkProps;
}

export const createImageForSlide = (slide: PptxGenJS.Slide, image: IImage, options: ISlideImageProps): void => {
	if (!image.src || !image.width || !image.height) {
		console.warn('Image is missing src, width or height');
		return;
	}

	const fallBackExtension = options.fallbackExtension || 'jpg';
	const widthInInch = pxToIn(image.width);
	const heightInInch = pxToIn(image.height);
	const aspectRatio = heightInInch / widthInInch;
	const destinationHeight = options.destinationHeight || aspectRatio;
	const scale = Math.min(options.destinationWidth / widthInInch, destinationHeight / heightInInch);

	slide.addImage({
		path: /\.(jpg|png|svg)$/.test(image.src) ? image.src : `${image.src}.${fallBackExtension}`,
		x: options.x || 0,
		y: options.y || 0,
		w: roundNumberToDoubleDecimal(widthInInch * scale),
		h: roundNumberToDoubleDecimal(heightInInch * scale),
		sizing: {
			type: options.type || 'contain',
			w: options.destinationWidth,
			h: options.destinationHeight || aspectRatio * options.destinationWidth
		},
		rounding: options.rounding,
		hyperlink: options.hyperlink
	});
};

export const createFullScreenCroppedImageForSlide = (slide: PptxGenJS.Slide, image: IImage): void => {
	createImageForSlide(slide, image, {
		destinationWidth: presentationDimensions.w,
		destinationHeight: presentationDimensions.h,
		type: 'cover'
	});
};

export const createPartnerLogoForSlide = (slide: PptxGenJS.Slide, image: IImage): void => {
	createImageForSlide(slide, image, {
		destinationWidth: 0.8,
		destinationHeight: 0.8,
		type: 'contain',
		x: '88%',
		y: '3%',
		fallbackExtension: 'png'
	});
};

export const getBadgeWidthFromText = (text: string): number => clamp(text.length * 0.1, 1.2, 6);

export const createBadgeForSlide = (slide: PptxGenJS.Slide, text: string, options: { x: number; y: number }): void => {
	const badeWidth = getBadgeWidthFromText(text);

	slide.addText(text.toUpperCase(), {
		...copyBold,
		lineSpacing: 0,
		shape: 'roundRect',
		x: options.x || 0,
		y: options.y || 0,
		w: badeWidth,
		h: elementSizes.badgeHeight,
		color: colors.blue,
		align: 'center',
		valign: 'middle',
		margin: 0,
		rectRadius: elementSizes.badgeHeight / 2,
		fill: { color: colors.yellow }
	});
};

export const logoImageOptions = {
	path: 'assets/images/logos/logo-jungfrau-ppt.svg',
	x: 0.3,
	y: 0.3,
	w: pxToIn(90),
	h: pxToIn(63)
};

export const createLogoForSlide = (slide: PptxGenJS.Slide): void => {
	slide.addImage(logoImageOptions);
};
